import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { LangContext } from "../i18n";
import Nav from "./Nav";
import Footer from "./Footer";
import WhatsAppFab from "./WhatsAppFab";

// In-page anchors that now live on their own routes
export const HASH_ROUTES = {
    "#contact": "/contact",
    "#products": "/products",
    "#about": "/about",
    "#installers": "/installers",
    "#gallery": "/gallery",
    "#faq": "/faq",
    "#warranty": "/warranty",
    "#cities": "/#cities",
};

const scrollToEl = (el) => {
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -64 });
    else el.scrollIntoView({ behavior: "smooth" });
};

function useUtmCapture() {
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid"];
            const captured = {};
            keys.forEach((k) => {
                const v = params.get(k);
                if (v) captured[k] = v;
            });
            if (Object.keys(captured).length > 0) {
                const stamped = { ...captured, at: new Date().toISOString() };
                if (!localStorage.getItem("da_utm_first")) localStorage.setItem("da_utm_first", JSON.stringify(stamped));
                localStorage.setItem("da_utm_last", JSON.stringify(stamped));
            }
        } catch (e) {}
    }, []);
}

export default function SiteLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const [lang, setLang] = useState(localStorage.getItem("da_lang") || "en");
    useUtmCapture();
    useEffect(() => {
        if (!sessionStorage.getItem("da_landing")) sessionStorage.setItem("da_landing", window.location.pathname);
    }, []);

    useEffect(() => {
        document.documentElement.lang = lang === "hi" ? "hi" : "en";
        localStorage.setItem("da_lang", lang);
    }, [lang]);

    // route change: jump to top (or to the hash target once it has rendered)
    useEffect(() => {
        const hash = (location.hash || "").split("?")[0];
        if (hash) {
            const timer = setTimeout(() => {
                const el = document.querySelector(hash);
                if (el) scrollToEl(el);
            }, 350);
            return () => clearTimeout(timer);
        }
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
        else window.scrollTo(0, 0);
    }, [location.pathname, location.hash]);

    // legacy hash anchors (#contact etc.) → smooth-scroll if present here, else route to their page
    useEffect(() => {
        const onClick = (e) => {
            const a = e.target.closest && e.target.closest('a[href^="#"]');
            if (!a) return;
            const href = a.getAttribute("href") || "";
            const hash = href.split("?")[0];
            e.preventDefault();
            if (hash === "#top" || hash === "#") {
                if (window.__lenis) window.__lenis.scrollTo(0);
                else window.scrollTo({ top: 0, behavior: "smooth" });
                return;
            }
            const el = document.querySelector(hash);
            if (el) {
                scrollToEl(el);
                return;
            }
            const route = HASH_ROUTES[hash];
            if (route) navigate(route + (href.includes("?") ? href.slice(href.indexOf("?")) : ""));
        };
        document.addEventListener("click", onClick);
        return () => document.removeEventListener("click", onClick);
    }, [navigate]);

    return (
        <LangContext.Provider value={{ lang, setLang }}>
            <div id="top" className="relative min-h-screen bg-mist text-charcoal">
                <div className="noise-overlay" aria-hidden="true" />
                <Nav />
                <main>
                    <Outlet />
                </main>
                <Footer />
                <WhatsAppFab />
            </div>
        </LangContext.Provider>
    );
}
